/**
 * Punto de reanudación. iPadOS cierra las apps en segundo plano con facilidad (2 GB de memoria),
 * así que cada cambio de pantalla se guarda al momento y al reabrir se vuelve al mismo sitio (§9.5).
 */
const KEY = 'atril.session.v1'

export interface SessionSnapshot {
  route: string
  savedAt: number
  boots: number
  /** Estado libre de la pantalla actual (sección, tempo…). */
  detail?: Record<string, unknown>
}

function read(): SessionSnapshot | null {
  try {
    const raw = localStorage.getItem(KEY)
    return raw ? (JSON.parse(raw) as SessionSnapshot) : null
  } catch {
    return null
  }
}

function write(snapshot: SessionSnapshot): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(snapshot))
  } catch {
    // Sin almacenamiento (modo privado): la app funciona, pero no reanuda.
  }
}

let current: SessionSnapshot | null = null
let previous: SessionSnapshot | null = null

/** Lee la sesión anterior y cuenta un arranque nuevo. Devuelve lo que había antes de arrancar. */
export function startSession(): SessionSnapshot | null {
  previous = read()
  current = {
    route: previous?.route ?? '/',
    savedAt: Date.now(),
    boots: (previous?.boots ?? 0) + 1,
  }
  if (previous?.detail) current.detail = previous.detail
  write(current)
  return previous
}

export function saveRoute(route: string): void {
  if (!current) return
  current = { ...current, route, savedAt: Date.now() }
  delete current.detail
  write(current)
}

export function saveDetail(detail: Record<string, unknown>): void {
  if (!current) return
  current = { ...current, detail, savedAt: Date.now() }
  write(current)
}

export function currentSession(): SessionSnapshot | null {
  return current
}

let resume: { route: string; detail: Record<string, unknown> | null } | null = null

/**
 * El estado libre de la pantalla (posición, tono…) tal como estaba al cerrarse la app, solo si se
 * vuelve a la misma ruta. Así, tras un cierre de iPadOS, Tocar sigue en el mismo acorde (§9.5).
 * La primera pantalla que lo pide decide, y las siguientes veces da lo mismo: React puede montar
 * dos veces un componente y no debe depender de quién llegó primero.
 */
export function takeResumeDetail(route: string): Record<string, unknown> | null {
  resume ??= { route, detail: previous?.route === route ? (previous.detail ?? null) : null }
  return resume.route === route ? resume.detail : null
}

/** La sesión tal como estaba antes de este arranque. */
export function previousSession(): SessionSnapshot | null {
  return previous
}
